import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getNotificationsPulse } from '../api'
import { notificationQueryKeys } from './notificationQueryKeys'
import { useAuth } from '@/hooks/use-auth'
import {
  INITIAL_PULSE,
  PULSE_INTERVAL_MS,
  shouldInvalidateListOnPulse,
  type PulseSnapshot,
} from '../utils/pulseUtils'
import { resetNotificationsPulse } from './refreshNotificationsPulse'

export type UseNotificationsPulseResult = PulseSnapshot & {
  isLoading: boolean
}

export const useNotificationsPulse = (): UseNotificationsPulseResult => {
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const headIdRef = useRef<string | null | undefined>(undefined)
  const [tabVisible, setTabVisible] = useState(
    () => typeof document === 'undefined' || !document.hidden
  )

  useEffect(() => {
    const onVisibilityChange = () => {
      setTabVisible(!document.hidden)
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated) {
      headIdRef.current = undefined
      resetNotificationsPulse(queryClient)
    }
  }, [isAuthenticated, queryClient])

  const { data: pulse = INITIAL_PULSE, isLoading } = useQuery({
    queryKey: notificationQueryKeys.pulse(),
    queryFn: async (): Promise<PulseSnapshot> => {
      const result = await getNotificationsPulse()
      if (result.status === 304) {
        return (
          queryClient.getQueryData<PulseSnapshot>(
            notificationQueryKeys.pulse()
          ) ?? INITIAL_PULSE
        )
      }

      const next: PulseSnapshot = {
        unreadCount: result.data.unreadCount,
        headId: result.data.headId,
      }

      if (shouldInvalidateListOnPulse(headIdRef.current, next.headId)) {
        void queryClient.invalidateQueries({
          queryKey: notificationQueryKeys.list(),
        })
      }
      headIdRef.current = next.headId

      return next
    },
    enabled: isAuthenticated && tabVisible,
    refetchInterval: isAuthenticated && tabVisible ? PULSE_INTERVAL_MS : false,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    staleTime: 0,
    retry: 1,
  })

  return {
    ...pulse,
    isLoading,
  }
}
